import { type Theme } from "@mui/material/styles";

import { ALERT_GLYPH, ALERT_ICON_MAPPING, type AlertSeverity } from "theme/components/alert-glyphs";
import { type ComponentOverrides } from "theme/theme.types";

/** Figma's `alert/indicator/w`: the severity stripe down the left edge. */
export const ALERT_INDICATOR_WIDTH = 8;

type AlertTone = { main: string; light: string };

/**
 * The stripe and glyph take `main`, the progress bar `light`. `inherit` is Figma's neutral grey; `info`
 * is not drawn in Figma and follows the other palette severities.
 */
export const alertTone = (theme: Theme, severity: AlertSeverity = "success"): AlertTone =>
  severity === "inherit"
    ? { main: theme.palette.grey[500], light: theme.palette.grey[400] }
    : theme.palette[severity];

/**
 * One surface for every severity — Figma's white card with a stripe — so MUI's tinted `standard`
 * background and text are replaced from `root`, which is appended after MUI's per-severity `variants`.
 * The icon colour is written from `root` too: MUI sets it as `.MuiAlert-root .MuiAlert-icon`, which an
 * `icon` slot override (one class) would lose to.
 */
export const MuiAlert: ComponentOverrides["MuiAlert"] = {
  defaultProps: {
    iconMapping: ALERT_ICON_MAPPING,
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => {
      const tone = alertTone(theme, ownerState.severity);

      return {
        ...theme.typography.body2,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.large,
        boxShadow: theme.shadows[2],
        borderLeft: `${ALERT_INDICATOR_WIDTH}px solid ${tone.main}`,
        padding: theme.spacing(2, 2, 2, 1),

        "& .MuiAlert-icon": {
          color: tone.main,
          marginRight: theme.spacing(1),
          padding: 0,
          fontSize: ALERT_GLYPH,
          opacity: 1,
        },

        "& .MuiAlert-message": {
          flex: 1,
          padding: 0,
        },

        /**
         * `ui/alert` puts a 24px icon button here where Figma draws a 16px box. The negative margins
         * centre the larger hit target on Figma's box, as MUI's own `marginRight: -8` does for its close
         * button; the 12px gap to the message is Figma's `alert/container/spacing`, less those 4px.
         */
        "& .MuiAlert-action": {
          padding: 0,
          margin: theme.spacing(-0.5, -0.5, -0.5, 1),
        },
      };
    },
  },
};

export const MuiAlertTitle: ComponentOverrides["MuiAlertTitle"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.h5,
      marginTop: 0,
      marginBottom: theme.spacing(0.5),
    }),
  },
};
